import React from 'react';

// Marca de Drive y del editor (Orvian Docs, Orvian Office…): se cambia con REACT_APP_BRAND al compilar.
export const BRAND = process.env.REACT_APP_BRAND || 'Orvian';
export const OFFICE_NAME = `${BRAND} Office`;
export const DRIVE_NAME = `${BRAND} Drive`;

// Colores de tipo de archivo afinados a la paleta Orvian (tonos medios, menos saturados que los de Google):
// se leen igual sobre el claro y el oscuro, así que no cambian con el tema.
export const FILE_COLORS = {
  doc: '#4d7cc9',
  sheet: '#2f9170',
  slides: '#c4861f',
  pdf: '#cf5a67',
  diagram: '#7a68d4',
  image: '#9c6ac8',
  form: '#7c4dbd',
  neutral: '#7f8497',
};

/* Catálogo de tipos que el producto sabe ABRIR y nombrar, no el menú de creación. `pdf` y
   `diagram` no se crean desde cero a propósito (un PDF en blanco no le sirve a nadie y no hay
   editor de diagramas propio): salen de archivos subidos, y de aquí toman su ícono, su color y su
   nombre en la lista de Drive y en la cabecera del editor. Quitarlos dejaría sin cara a todos los
   PDF y .vsdx que ya hay guardados. Lo que se puede crear está en `accionesNuevo()` de Drive.jsx. */
export const OFFICE_APPS = {
  doc: { name: `${BRAND} Docs`, color: FILE_COLORS.doc, noun: 'documento', theNoun: 'el documento', thisNoun: 'este documento' },
  sheet: { name: `${BRAND} Sheets`, color: FILE_COLORS.sheet, noun: 'hoja de cálculo', theNoun: 'la hoja', thisNoun: 'esta hoja' },
  slides: { name: `${BRAND} Slides`, color: FILE_COLORS.slides, noun: 'presentación', theNoun: 'la presentación', thisNoun: 'esta presentación' },
  pdf: { name: `${BRAND} PDF`, color: FILE_COLORS.pdf, noun: 'PDF', theNoun: 'el PDF', thisNoun: 'este PDF' },
  diagram: { name: `${BRAND} Diagramas`, color: FILE_COLORS.diagram, noun: 'diagrama', theNoun: 'el diagrama', thisNoun: 'este diagrama' },
  text: { name: `${BRAND} Texto`, color: FILE_COLORS.neutral, noun: 'archivo de texto', theNoun: 'el archivo', thisNoun: 'este archivo' },
  form: { name: `${BRAND} Forms`, color: FILE_COLORS.form, noun: 'formulario', theNoun: 'el formulario', thisNoun: 'este formulario' },
};

/** Ícono de cada app del editor: hoja con esquina doblada y glifo del tipo. */
export function ProductIcon({ kind = 'doc', size = 36 }) {
  const color = OFFICE_APPS[kind]?.color || OFFICE_APPS.doc.color;
  return (
    <svg width={Math.round(size * 0.78)} height={size} viewBox="0 0 28 36" aria-hidden="true">
      <path d="M3 0h15.5L28 9.5V33a3 3 0 0 1-3 3H3a3 3 0 0 1-3-3V3a3 3 0 0 1 3-3z" fill={color} />
      <path d="M18.5 0 28 9.5h-6.5a3 3 0 0 1-3-3z" fill="#fff" fillOpacity=".4" />
      {kind === 'sheet' && (
        <g fill="none" stroke="#fff" strokeWidth="1.6">
          <rect x="6.5" y="15" width="15" height="12" rx="1" />
          <path d="M6.5 19h15M6.5 23h15M12 15v12" />
        </g>
      )}
      {kind === 'slides' && <rect x="6.5" y="16" width="15" height="10" rx="1.5" fill="none" stroke="#fff" strokeWidth="1.8" />}
      {kind === 'pdf' && (
        <text x="14" y="26.5" textAnchor="middle" fontSize="8.5" fontWeight="700" fontFamily="Arial, sans-serif" fill="#fff">PDF</text>
      )}
      {kind === 'diagram' && (
        <g fill="#fff" stroke="#fff" strokeWidth="1.4">
          <rect x="6" y="14.5" width="6" height="5" rx="1" />
          <rect x="16" y="14.5" width="6" height="5" rx="1" />
          <rect x="11" y="24" width="6" height="5" rx="1" />
          <path d="M12 17h4M9 19.5v2.5h5v2M19 19.5v2.5h-5" fill="none" />
        </g>
      )}
      {kind === 'form' && (
        <g fill="#fff">
          <circle cx="8.5" cy="16" r="1.6" />
          <rect x="12" y="15" width="9.5" height="2" rx="1" />
          <circle cx="8.5" cy="21" r="1.6" />
          <rect x="12" y="20" width="9.5" height="2" rx="1" />
          <circle cx="8.5" cy="26" r="1.6" />
          <rect x="12" y="25" width="9.5" height="2" rx="1" />
        </g>
      )}
      {kind === 'doc' && (
        <g fill="#fff">
          <rect x="6.5" y="15" width="15" height="2" rx="1" />
          <rect x="6.5" y="20" width="15" height="2" rx="1" />
          <rect x="6.5" y="25" width="10" height="2" rx="1" />
        </g>
      )}
    </svg>
  );
}

/**
 * Logotipo de Aurora IA. Es el MISMO archivo que usa la app del teléfono
 * (`app/assets/brand/aurora.png`), para que la IA se reconozca igual en los dos
 * sitios. `mono` lo pinta en blanco, para cuando va sobre un botón de color.
 */
export function AuroraLogo({ size = 20, mono = false }) {
  return (
    <img
      src="/brand/aurora.png"
      width={size}
      height={size}
      alt=""
      aria-hidden="true"
      style={{
        display: 'block',
        width: size,
        height: size,
        objectFit: 'contain',
        flex: 'none',
        ...(mono ? { filter: 'brightness(0) invert(1)' } : null),
      }}
    />
  );
}
